import { FC, memo, useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { FlexboxGrid, Panel } from 'rsuite'
import Image from 'next/legacy/image'

import Img from '../../Img'
import { debounce } from '../../../utils/event-utils'
import { imageLoader } from '../../../utils/images'
import { Banner } from '../../../utils/types/misc'
import { TOTAL_SATISFIED_CUSTOMERS } from '../../../utils/constants'
import useIsOnMobile from '../../../services/useIsOnMobile'
import { Product } from '../../../utils/types/Product'

import styles from '../../../styles/Banner.module.less'

interface BannerCardProps {
  banner: Banner
  products?: Product[]
}

const BannerCard: FC<BannerCardProps> = (props) => {
  const { banner, products } = props
  const { t } = useTranslation('marketing')

  const isOnMobile = useIsOnMobile()

  const [startColumn, setStartColumn] = useState<number | 'right-most'>(1)
  const [gridWidth, setGridWidth] = useState<number>(0)
  const [totalColumnsState, setTotalColumnsState] = useState(0)

  useEffect(() => {
    const startCol = banner.startColumn
    const columnSpan = banner.spanX

    // Grid element that contains the css variables
    const gridElement = document.querySelector('#product-grid')

    if (!gridElement) {
      // Could not find grid element
      return
    }

    const productCardWidthString: string = getComputedStyle(gridElement)
      .getPropertyValue('--product-grid-card-width')
    const gapWidthString: string = getComputedStyle(gridElement)
      .getPropertyValue('--product-grid-gap')

    // Prevent width 0 => infinity calculation
    const productCardWidth: number = Math.max(Number(productCardWidthString), 1)
    const gapWidth: number = Math.max(Number(gapWidthString), 1)

    const getTotalColumn = () => {
      // Extra width for a simpler calculation
      const gridWidthWithExtraGap = gridWidth + gapWidth
      const columnWithGapWidth = productCardWidth + gapWidth

      const totalColumn = Math.floor(gridWidthWithExtraGap / columnWithGapWidth)
      return totalColumn
    }

    const totalColumns = getTotalColumn()
    setTotalColumnsState(totalColumns)

    if (totalColumns === 1) {
      setStartColumn(1)
      return
    }

    const span = columnSpan === 'full' ? 0 : columnSpan

    if (startCol === 'right-most') { // Right most starting position
      // Shouldn't be less than 1
      const decideStartColumn = (totalColumns - span + 1) < 1 ? 3 : (totalColumns - span + 1)
      setStartColumn(decideStartColumn) // 1->2 means spanning two columns, therefore +1 needed
    } else if (Number(startCol) + span > totalColumns) {
      // Desired position would break out of bounds
      setStartColumn(totalColumns - span + 1)
    } else {
      setStartColumn(startCol)
    }
  }, [gridWidth])

  const getGridPositioningRules = (b: Banner) => {
    if (b.spanX === 'full') {
      return {
        gridRow: b.startRow,
      }
    }
    if (b.spanY === 'full') {
      return {
        gridColumn: +b.startColumn,
      }
    }

    const spanX = isOnMobile || totalColumnsState === 1 ? 2 : b.spanX

    return {
      gridArea: `${b.startRow} / ${isOnMobile ? 1 : startColumn} / span ${b.spanY} / span ${spanX}`,
    }
  }

  const handleResize = () => {
    const selected = document.querySelector('#product-grid')
    if (selected) {
      const width: number = (selected as HTMLElement).offsetWidth
      setGridWidth(width)
    }
  }

  const debouncedHandleResize = debounce(handleResize, 100)

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', debouncedHandleResize)
    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  }, [])

  const { image: bannerImage } = banner

  const getColSpan = () => {
    if (bannerImage) {
      if (isOnMobile) return 14
      return 12
    }

    return 24
  }

  const bannerTitle = t(banner.title, { count: TOTAL_SATISFIED_CUSTOMERS })
  const { bordered = true, className = '' } = banner

  return (
    <div
      className={`${styles[banner.type]} ${styles[`span-x-${banner.spanX}`] || ''} ${styles[`span-y-${banner.spanY}`] || ''} ${className}`}
      data-card-type={banner.type}
      style={getGridPositioningRules(banner)}
    >
      <Panel
        bordered={bordered}
        bodyFill
      >
        <FlexboxGrid align="middle">
          <FlexboxGrid.Item
            colspan={getColSpan()}
            className={`
              ${banner.type === 'banner-tile-left' ? 'padding-right-spacer-tripple' : ''}
              ${banner.type === 'banner-tile-center' ? 'text-center' : ''}
              `}
          >
            {banner.graphics && (
              <div className="margin-bottom-spacer">
                <Image
                  alt={bannerTitle}
                  src={banner.graphics}
                  height={100}
                  width={100}
                />
              </div>
            )}
            {bannerTitle && <h4 className={`${styles.title} margin-bottom-spacer-double`}>{bannerTitle}</h4>}
            <div className={styles.content}>
              {banner.content(t, <></>, products)}
            </div>
          </FlexboxGrid.Item>
          {bannerImage && (
            <FlexboxGrid.Item
              colspan={24 - getColSpan()}
              className={styles['banner-image']}
            >
              <Img
                loader={imageLoader}
                src={bannerImage}
                layout="fill"
                alt={banner.title}
              />
            </FlexboxGrid.Item>
          )}
        </FlexboxGrid>
      </Panel>
    </div>
  )
}

export default memo(BannerCard)
